import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, Modal, Stack, Tooltip, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { AxiosResponse } from 'axios';
import React from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useHistory, useLocation } from 'react-router-dom';
import { CourseApi } from '../api/CourseApi';
import { IDialog } from '../api/DialogApi';
import { DialogPrompt, DialogPromptApi, DialogPromptConversationListen, DialogPromptConversationListenSpeak, DialogPromptConversationListenWithoutCharacter, DialogPromptConversationReadSpeak, DialogPromptConversationReadWithBlankSpeak, DialogPromptConversationSpeakOnly, DialogPromptFactory, DialogPromptFeedbackButton, DialogPromptImage, DialogPromptImageSelectSpeak, DialogPromptIntroduceWord, DialogPromptLearnWithCharacter, DialogPromptListenSpeak, DialogPromptListenSpeakNoGrade, DialogPromptMatchGame, DialogPromptMatchWordPicture, DialogPromptQuizQuestion, DialogPromptQuizQuestionAudio, DialogPromptQuizQuestionSpeak, DialogPromptQuizWord, DialogPromptScoringLevel, DialogPromptSelectAudioSpeed, DialogPromptSelectNextDialog, DialogPromptSelectTeacher, DialogPromptSpeakNoGrade, DialogPromptSpeakWithGrade, DialogPromptStartClass, DialogPromptStartNextLesson, DialogPromptSummary, DialogPromptTypeSpeak, DialogPromptTypeSpeakV2, isDeprecatedDialogPromptClassName } from '../api/DialogPromptApi';
import { LessonApi } from '../api/LessonApi';
import { IUserDialogPrompt, UserDialogPromptApi, UserDialogPromptUserSummary } from '../api/UserDialogPromptApi';
import DropDownArrow from '../asset/dropdown_arrow.png';
import LeftArrowBlueIcon from '../asset/icon-left-arrow-blue.png';
import AppBarContext from '../context/AppBarContext';
import DialogPromptFooterContext from '../context/DialogPromptFooterContext';
import { SentenceScoreDialogProvider } from '../context/SentenceScoreDialogContext';
import { handleErrorMessage } from './ErrorDialog';
import SentenceScoreDialog from './SentenceScoreDialog';
import DialogPromptComponent, { ContinueDialogFromBefore } from './dialogPrompt/DialogPromptComponent';
import DialogPromptConversationListenComponent from './dialogPrompt/DialogPromptConversationListenComponent';
import DialogPromptConversationRelatedComponent from './dialogPrompt/DialogPromptConversationRelatedComponent';
import DialogPromptEnterNameComponent from './dialogPrompt/DialogPromptEnterNameComponent';
import DialogPromptFeedbackButtonComponent from './dialogPrompt/DialogPromptFeedbackButtonComponent';
import DialogPromptImageComponent from './dialogPrompt/DialogPromptImageComponent';
import DialogPromptImageSelectSpeakComponent from './dialogPrompt/DialogPromptImageSelectSpeakComponent';
import DialogPromptIntroduceWordComponent from './dialogPrompt/DialogPromptIntroduceWordComponent';
import DialogPromptLearnWithCharacterComponent from './dialogPrompt/DialogPromptLearnWithCharacterComponent';
import DialogPromptListenSpeakComponent from './dialogPrompt/DialogPromptListenSpeakComponent';
import DialogPromptListenSpeakNoGradeComponent from './dialogPrompt/DialogPromptListenSpeakNoGradeComponent';
import DialogPromptMatchGameComponent from './dialogPrompt/DialogPromptMatchGameComponent';
import DialogPromptMatchWordPictureComponent from './dialogPrompt/DialogPromptMatchWordPictureComponent';
import DialogPromptQuizQuestionAudioComponent from './dialogPrompt/DialogPromptQuizQuestionAudioComponent';
import DialogPromptQuizQuestionComponent from './dialogPrompt/DialogPromptQuizQuestionComponent';
import DialogPromptQuizQuestionSpeakComponent from './dialogPrompt/DialogPromptQuizQuestionSpeakComponent';
import DialogPromptQuizWordComponent from './dialogPrompt/DialogPromptQuizWordComponent';
import DialogPromptScoringLevelComponent from './dialogPrompt/DialogPromptScoringLevelComponent';
import DialogPromptSelectAudioSpeedComponent from './dialogPrompt/DialogPromptSelectAudioSpeedComponent';
import DialogPromptSelectAvatarComponent from './dialogPrompt/DialogPromptSelectAvatarComponent';
import DialogPromptSelectNextDialogComponent from './dialogPrompt/DialogPromptSelectNextDialogComponent';
import DialogPromptSelectTeacherComponent from './dialogPrompt/DialogPromptSelectTeacherComponent';
import DialogPromptSpeakNoGradeComponent from './dialogPrompt/DialogPromptSpeakNoGradeComponent';
import DialogPromptSpeakWithGradeComponent from './dialogPrompt/DialogPromptSpeakWithGradeComponent';
import DialogPromptStartClassComponent from './dialogPrompt/DialogPromptStartClassComponent';
import DialogPromptStartNextLessonComponent from './dialogPrompt/DialogPromptStartNextLessonComponent';
import { DialogPromptSummaryComponent } from './dialogPrompt/DialogPromptSummaryComponent';
import DialogPromptTypeSpeakComponent from './dialogPrompt/DialogPromptTypeSpeakComponent';
import DialogPromptTypeSpeakV2Component from './dialogPrompt/DialogPromptTypeSpeakV2Component';
import { scrollElementIntoViewWithFallback } from '../util/scrollUtils';

const inactiveDialogPromptFooterContextValue = {
    leftNavigationAlwaysList: [] as React.ReactNode[],
    setLeftNavigationAlwaysList: (_leftNavigationAlwaysList: React.ReactNode[]) => undefined,
    leftNavigationList: [] as React.ReactNode[],
    setLeftNavigationList: (_leftNavigationList: React.ReactNode[]) => undefined,
    rightNavigationList: [] as React.ReactNode[],
    setRightNavigationList: (_rightNavigationList: React.ReactNode[]) => undefined,
    actionList: [] as React.ReactNode[],
    setActionList: (_actionList: React.ReactNode[]) => undefined,
    clearFooterList: () => undefined,
};

const FEEDBACK_PROMPT_CLASS_NAME = 'DialogPromptFeedbackButtonModel';
const FEEDBACK_PROMPT_FORM_URL = 'https://forms.gle/6R8hjRKcpvmx5WQGA';
const feedbackPromptTagRegex = /<Feedback\/>/i;

const activatesFeedbackButton = (dialogPrompt: DialogPrompt | null | undefined): boolean => (
    dialogPrompt?.class_name === FEEDBACK_PROMPT_CLASS_NAME ||
    feedbackPromptTagRegex.test(dialogPrompt?.prompt ?? '')
);

const FeedbackFooterButton: React.FC<{ shakeNonce: number }> = ({ shakeNonce }) => {
    const animationName = shakeNonce % 2 === 0 ? 'dialogFeedbackWobbleA' : 'dialogFeedbackWobbleB';
    const handleFeedbackClick = React.useCallback(() => {
        const feedbackWindow = window.open(FEEDBACK_PROMPT_FORM_URL, '_blank', 'noopener,noreferrer');
        if (feedbackWindow) {
            feedbackWindow.opener = null;
        }
    }, []);

    return (
        <Tooltip title="Send feedback" arrow>
            <Button
                aria-label="Send feedback"
                data-testid="dialog-feedback-button"
                endIcon={<FeedbackOutlinedIcon />}
                onClick={handleFeedbackClick}
                size="small"
                variant="outlined"
                sx={{
                    minWidth: 0,
                    height: 32,
                    px: 0.75,
                    py: 0,
                    color: 'primary.main',
                    borderColor: 'primary.main',
                    borderRadius: '16px',
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: 0,
                    lineHeight: 1,
                    textTransform: 'none',
                    transformOrigin: '50% 50%',
                    '& .MuiButton-endIcon': {
                        ml: 0.5,
                        mr: 0,
                        '& svg': {
                            fontSize: 16,
                        },
                    },
                    '@keyframes dialogFeedbackWobbleA': {
                        '0%, 100%': { transform: 'rotate(0deg) scale(1)', boxShadow: 'none' },
                        '12%': { transform: 'rotate(-18deg) scale(1.14)', boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.45)' },
                        '24%': { transform: 'rotate(16deg) scale(1.14)', boxShadow: '0 0 0 8px rgba(25, 118, 210, 0.16)' },
                        '36%': { transform: 'rotate(-12deg) scale(1.10)', boxShadow: '0 0 0 12px rgba(25, 118, 210, 0.08)' },
                        '48%': { transform: 'rotate(9deg) scale(1.08)', boxShadow: '0 0 0 16px rgba(25, 118, 210, 0)' },
                        '60%': { transform: 'rotate(-6deg) scale(1.05)' },
                        '72%': { transform: 'rotate(4deg) scale(1.03)' },
                    },
                    '@keyframes dialogFeedbackWobbleB': {
                        '0%, 100%': { transform: 'rotate(0deg) scale(1)', boxShadow: 'none' },
                        '12%': { transform: 'rotate(18deg) scale(1.14)', boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.45)' },
                        '24%': { transform: 'rotate(-16deg) scale(1.14)', boxShadow: '0 0 0 8px rgba(25, 118, 210, 0.16)' },
                        '36%': { transform: 'rotate(12deg) scale(1.10)', boxShadow: '0 0 0 12px rgba(25, 118, 210, 0.08)' },
                        '48%': { transform: 'rotate(-9deg) scale(1.08)', boxShadow: '0 0 0 16px rgba(25, 118, 210, 0)' },
                        '60%': { transform: 'rotate(6deg) scale(1.05)' },
                        '72%': { transform: 'rotate(-4deg) scale(1.03)' },
                    },
                    '@media (prefers-reduced-motion: no-preference)': shakeNonce > 0 ? {
                        animation: `${animationName} 0.95s ease-in-out 0s 2`,
                    } : {},
                }}
            >
                Feedback
            </Button>
        </Tooltip>
    );
};

const DialogPromptFooterScope: React.FC<{ isActive: boolean; children: React.ReactNode }> = ({ isActive, children }) => {
    const dialogPromptFooterContextValue = React.useContext(DialogPromptFooterContext);

    return (
        <DialogPromptFooterContext.Provider value={isActive ? dialogPromptFooterContextValue : inactiveDialogPromptFooterContextValue}>
            {children}
        </DialogPromptFooterContext.Provider>
    );
};

interface DialogViewProps {
    dialog: IDialog;
    updateUserDialogPromptSummary: (summaryList: UserDialogPromptUserSummary[]) => void;
    nextDialogPrompt: DialogPrompt | null;
}

const DialogView: React.FC<DialogViewProps> = ({ dialog, updateUserDialogPromptSummary, nextDialogPrompt }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const { leftNavigationAlwaysList, setLeftNavigationAlwaysList,
        leftNavigationList,
        rightNavigationList,
        actionList,
        clearFooterList } = React.useContext(DialogPromptFooterContext)


    const [, setCourseName] = React.useState("");
    const [, setLessonName] = React.useState("");
    const [, setDialogTitle] = React.useState(dialog.title);
    const [, setDialogSubtitle] = React.useState(dialog.subtitle);

    const [dialogPromptList, setDialogPromptList] = React.useState<DialogPrompt[]>([]);
    const [currentDialogPromptIndex, setCurrentDialogPromptIndex] = React.useState(1);

    const [originalDialogPromptIdList, setOriginalDialogPromptIdList] = React.useState<number[]>([]);
    const [currentOriginalDialogPromptIdIndex, setCurrentOriginalDialogPromptIdIndex] = React.useState(1);

    const chatBubbleContainerRef = React.useRef<HTMLDivElement>(null);
    const scrollContainerRef = React.useRef<HTMLDivElement>(null);
    const lastScrollTopRef = React.useRef(0);
    const lastUpScrollLogAtRef = React.useRef(0);

    const [isModalOpen, setModalOpen] = React.useState(false);

    const [noteToStudent, setNoteToStudent] = React.useState<(string | null)[]>([]);
    const [showScrollToBottom, setShowScrollToBottom] = React.useState(false);
    const [feedbackShakeNonce, setFeedbackShakeNonce] = React.useState(0);
    const feedbackStartShakeKeyRef = React.useRef<string | null>(null);
    const feedbackEndShakeKeyRef = React.useRef<string | null>(null);
    const activeDialogPrompt = dialogPromptList[currentDialogPromptIndex - 1] ?? null;
    const activeDialogPromptId = activeDialogPrompt?.id ?? null;
    const activeDialogPromptActivatesFeedback = activatesFeedbackButton(activeDialogPrompt);
    const hasFeedbackPrompt = React.useMemo(
        () => dialogPromptList.some(activatesFeedbackButton),
        [dialogPromptList],
    );

    React.useEffect(() => {
        let shouldShakeForFeedbackPrompt = false;
        let shouldShakeForDialogEnd = false;

        if (activeDialogPromptActivatesFeedback && activeDialogPromptId != null) {
            const startShakeKey = `${dialog.id}:${activeDialogPromptId}:feedback-prompt`;
            if (feedbackStartShakeKeyRef.current !== startShakeKey) {
                feedbackStartShakeKeyRef.current = startShakeKey;
                shouldShakeForFeedbackPrompt = true;
            }
        }

        const isAtDialogEnd = hasFeedbackPrompt && dialogPromptList.length > 0 && currentDialogPromptIndex >= dialogPromptList.length;
        if (isAtDialogEnd) {
            const endShakeKey = `${dialog.id}:${dialogPromptList[dialogPromptList.length - 1]?.id ?? 'end'}:dialog-end`;
            if (feedbackEndShakeKeyRef.current !== endShakeKey) {
                feedbackEndShakeKeyRef.current = endShakeKey;
                shouldShakeForDialogEnd = true;
            }
        }

        if (shouldShakeForFeedbackPrompt || shouldShakeForDialogEnd) {
            setFeedbackShakeNonce(nonce => nonce + (shouldShakeForFeedbackPrompt && shouldShakeForDialogEnd ? 3 : 1));
        }
    }, [
        activeDialogPromptActivatesFeedback,
        activeDialogPromptId,
        currentDialogPromptIndex,
        dialog.id,
        dialogPromptList,
        hasFeedbackPrompt,
    ]);


    const addDialogPromptList = React.useCallback(async (list: DialogPrompt[], append = false) => {

        if (!list || list.length === 0) return;

        // remove completed prompts, or upto last completed prompt.

        // get user dialog prompt data
        const dialogId = list[0].dialog_id;
        let userDialogPromptList: IUserDialogPrompt[] = [];

        try {
            const response = await UserDialogPromptApi.getAll(dialogId);
            userDialogPromptList = response.data;
        } catch (err) {
            handleErrorMessage(err, setErrorMessage, history, location);
            return;
        }

        // these don't need to be completed.
        // there is another skip dialog list in user_dialog_prompt_model.py
        const SKIPPABLE = new Set([
            'DialogPromptStartNextLessonModel',
            'DialogPromptSummaryModel',
            'DialogPromptSelectNextDialogModel',
            'DialogPromptStartClassModel',
            FEEDBACK_PROMPT_CLASS_NAME,
        ]);

        const coalesceTime = (udp: IUserDialogPrompt) => udp.updated_at ? udp.updated_at.getTime() : udp.created_at.getTime();

        // ----- compute first_incomplete_prompt_i and max_dialog_prompt_i ----------
        let firstIncompletePromptIndex: number | null = null;
        let maxDateTimeIndex: number | null = null;
        let maxDateTime: number | null = null;

        for (let i = 0; i < list.length; i++) {
            const thisDialogPrompt = list[i];
            const thisUserDialogPromptList = userDialogPromptList.filter(udp => udp.dialog_prompt_id == thisDialogPrompt.id)
            if (thisUserDialogPromptList.every(udp => udp.is_completed == null || udp.is_completed == false)) {
                if (firstIncompletePromptIndex == null) {
                    firstIncompletePromptIndex = i
                }
            } else {
                const thisMaxDateTime = thisUserDialogPromptList.length > 0 ? Math.max(...thisUserDialogPromptList.map(coalesceTime)) : null;
                if (thisMaxDateTime != null) {
                    if (maxDateTime == null || thisMaxDateTime > maxDateTime) {
                        maxDateTimeIndex = i
                        maxDateTime = thisMaxDateTime
                    }
                }
            }
        }

        // ----- build slices
        const incomplete_list = firstIncompletePromptIndex == null ? [] : list.slice(firstIncompletePromptIndex).filter(dp => !SKIPPABLE.has(dp.class_name));
        const after_max_list = maxDateTimeIndex == null ? [] : list.slice(maxDateTimeIndex + 1).filter(dp => !SKIPPABLE.has(dp.class_name));


        let addContinuePrompt = false
        if (incomplete_list.length > 0 && firstIncompletePromptIndex != null) {
            list = list.slice(firstIncompletePromptIndex);
            if (firstIncompletePromptIndex > 0) {
                addContinuePrompt = true
            }
        } else if (after_max_list.length > 0 && maxDateTimeIndex != null) {
            list = list.slice(maxDateTimeIndex + 1);
            if (maxDateTimeIndex > 0) {
                addContinuePrompt = true
            }
        }
        if (addContinuePrompt) {
            // add a message that we're starting in the middle somewhere.
            const dialogPrompt = new DialogPrompt(
                -`${Date.now()}`, // create a unique key so the prompt is always re-rendered
                list[0].dialog_id,
                list[0].order_index - 1,
                true,
                ContinueDialogFromBefore,
                false,
                false
            );
            list = [dialogPrompt, ...list];
        }

        // refetch some prompts
        const finalList = list.map(async (prompt) => {
            if (prompt.class_name == "DialogPromptMatchWordPictureModel" ||
                prompt.class_name == "DialogPromptMatchGameModel" ||
                prompt.class_name == "DialogPromptTypeSpeakModel" ||
                prompt.class_name == "DialogPromptTypeSpeakV2Model" ||
                prompt.class_name == "DialogPromptImageSelectSpeakModel" ||
                prompt.class_name == "DialogPromptQuizQuestionModel" ||
                prompt.class_name == "DialogPromptQuizQuestionAudioModel" ||
                prompt.class_name == "DialogPromptQuizQuestionSpeakModel" ||
                prompt.class_name == "DialogPromptConversationListenModel" ||
                prompt.class_name == "DialogPromptConversationListenSpeakModel" ||
                prompt.class_name == "DialogPromptConversationListenWithoutCharacterModel" ||
                prompt.class_name == "DialogPromptConversationReadSpeakModel" ||
                prompt.class_name == "DialogPromptConversationReadWithBlankSpeakModel" ||
                prompt.class_name == "DialogPromptConversationSpeakOnlyModel" ||
                prompt.class_name == "DialogPromptSpeakNoGradeModel" ||
                prompt.class_name == "DialogPromptSpeakWithGradeModel") {
                try {
                    const response = await DialogPromptApi.getById(prompt.id) as AxiosResponse<DialogPrompt>;
                    // console.log('Type of addDialogPromptList.getById():', response.data.constructor.name);
                    // const dialogPrompt: DialogPrompt = response.data as DialogPrompt;
                    // console.log('Type of addDialogPromptList.dialogPrompt:', dialogPrompt.constructor.name);
                    return response.data; // Replace the prompt with the data obtained from the API
                } catch (err: any) {
                    if (err.response && err.response.status === 401) {
                        handleErrorMessage(err, setErrorMessage, history, location);

                    } else {
                        console.error(err)
                        handleErrorMessage(err, setErrorMessage, history, location);
                    }
                    return prompt;
                }
            }
            return prompt; // Keep other prompts as they are
        });

        // Wait for all the API calls to resolve
        Promise.all(finalList)
            .then((updatedList) => {
                const idList = updatedList.map((prompt) => prompt.id);
                if (append) {
                    setDialogPromptList((prevList) => [...prevList, ...updatedList]);
                    setOriginalDialogPromptIdList((prevList) => [...prevList, ...idList]);
                } else {
                    setDialogPromptList(updatedList);
                    setOriginalDialogPromptIdList(idList);
                    setCurrentDialogPromptIndex(1);
                }
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            });
    }, [setErrorMessage, history, location, setDialogPromptList, setOriginalDialogPromptIdList, setCurrentDialogPromptIndex]);
    React.useEffect(() => {

        setCurrentDialogPromptIndex(1)

        setDialogTitle(dialog.title);
        setDialogSubtitle(dialog.subtitle);

        LessonApi.getById(dialog.lesson_id)
            .then(response => {
                if (response.status < 300) {
                    return response.data;
                }
                throw new Error("Unable to LessonApi.getById() " + response.status)
            })
            .then(lesson => {
                setLessonName(lesson.name)
                CourseApi.getById(lesson.course_id)
                    .then(response => {
                        if (response.status < 300) {
                            return response.data;
                        }
                        throw new Error("Unable to CourseApi.getById() " + response.status)
                    })
                    .then(course => setCourseName(course.name))
                    .catch(err => {
                        handleErrorMessage(err, setErrorMessage, history, location);
                    })
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })

        DialogPromptApi.getAll(dialog.id)
            .then(response => {
                if (response.status < 300) {
                    return response.data;
                }
                throw new Error("Unable to DialogPromptApi.getAll() " + response.status)

            })
            .then(async list => {
                const visibleList = list.filter(dialog => dialog.is_hidden === false && !isDeprecatedDialogPromptClassName(dialog.class_name));
                await addDialogPromptList(visibleList);
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })

    }, [dialog, addDialogPromptList, history, location, setErrorMessage]);

    React.useEffect(() => {
        if (nextDialogPrompt != null && dialogPromptList.length > 0) {
            // console.log("nextDialogPrompt", nextDialogPrompt, "dialogPromptList", dialogPromptList)
            const index = dialogPromptList.findIndex(p => p.id === nextDialogPrompt.id);
            // Only update if index is valid and list actually needs slicing
            if (index > 0) {
                const newList = dialogPromptList.slice(index);
                setDialogPromptList(newList);
            }
        }
    }, [nextDialogPrompt, dialogPromptList])

    React.useEffect(() => {

        const displayedList = dialogPromptList.slice(0, currentDialogPromptIndex);

        // Loop through the filtered list and add non-empty note_to_student values
        const nonEmptyNotes = displayedList
            .filter(prompt => prompt.is_note_to_student)
            .map(prompt => prompt.prompt);
        setNoteToStudent(nonEmptyNotes)

    }, [dialogPromptList, currentDialogPromptIndex])

    React.useLayoutEffect(() => {
        clearFooterList();
    }, [activeDialogPromptId, clearFooterList]);

    const handleScroll = React.useCallback(() => {
        const el = scrollContainerRef.current;
        if (!el) return;
        const prevTop = lastScrollTopRef.current;
        const { scrollTop, scrollHeight, clientHeight } = el;
        const movingUp = scrollTop < prevTop;
        const movingDown = scrollTop > prevTop;
        const atBottom = scrollHeight - scrollTop - clientHeight < 60; // threshold similar to ConversationInterface
        const now = Date.now();
        if (movingUp && (now - lastUpScrollLogAtRef.current > 200)) {
            lastUpScrollLogAtRef.current = now;
            if (el.dataset.agentAutoScrollLock !== '1') {
                el.dataset.agentAutoScrollLock = '1';
            }
        }
        if (movingDown && atBottom && el.dataset.agentAutoScrollLock === '1') {
            el.dataset.agentAutoScrollLock = '0';
        }
        lastScrollTopRef.current = scrollTop;
        setShowScrollToBottom(!atBottom);
    }, []);

    React.useEffect(() => {
        if (scrollContainerRef.current?.dataset.agentAutoScrollLock === '1') {
            handleScroll();
            return;
        }
        scrollElementIntoViewWithFallback(chatBubbleContainerRef.current, scrollContainerRef.current);
        // Re-evaluate scroll position after content changes
        handleScroll();
    }, [currentDialogPromptIndex, handleScroll]);

    // Initialize button state on mount
    React.useEffect(() => {
        handleScroll();
    }, [handleScroll]);

    const setDialogPromptCompleteImpl = (dialogPromptId: number) => {

        // check the dialogPromptId, old dialogs can be clicked on.
        const currentDialogPrompt = dialogPromptList[currentDialogPromptIndex - 1];
        if (currentDialogPrompt && currentDialogPrompt.id == dialogPromptId) {
            clearFooterList();

            // dialogPrormptId == -1 if "lets continue" prompt, so don't complete it
            if (dialogPromptId >= 0) {
                UserDialogPromptApi.completeDialogPrompt(dialogPromptId)
                    .then(response => {
                        if (response.status < 300) {
                            return response.data;
                        }
                        throw new Error("Unable to UserDialogPromptApi.completeDialogPrompt " + response.status)

                    })
                    .then(summary => {
                        updateUserDialogPromptSummary(summary)
                    })
                    .catch(err => {
                        handleErrorMessage(err, setErrorMessage, history, location);
                    })

            }

            // currentDialogPromptIndex should always be right ... but search by ID anyway
            const slicedDialogPromptList = dialogPromptList.slice(currentDialogPromptIndex - 1);

            // Use findIndex() on the sliced array to find the next index of the dialogPromptId
            const indexOfTargetId = slicedDialogPromptList.findIndex((dialogPrompt) => dialogPrompt.id === dialogPromptId);

            // If the indexOfTargetId is not -1 (i.e., the targetId is found), adjust the index based on the currentDialogPromptIndex
            const finalIndex = indexOfTargetId !== -1 ? indexOfTargetId + currentDialogPromptIndex - 1 : -1;
            if (finalIndex >= 0) {
                // +1 because 1 based, +1 to advance to next dialog prompt
                setCurrentDialogPromptIndex(finalIndex + 2);
            } else {
                // if not found, just goto next index
                setCurrentDialogPromptIndex((prevIndex) => (prevIndex + 1));
            }

            // also update index of original dialog prompt
            const indexOfId = originalDialogPromptIdList.indexOf(dialogPromptId);
            // +1 because 1 based, +1 to advance to next dialog prompt
            setCurrentOriginalDialogPromptIdIndex(indexOfId + 2);
        }

    }

    const setDialogPromptCompleteRef = React.useRef(setDialogPromptCompleteImpl);
    setDialogPromptCompleteRef.current = setDialogPromptCompleteImpl;
    const setDialogPromptComplete = React.useCallback((id: number) => setDialogPromptCompleteRef.current(id), []);

    const [openGoBackDialog, setOpenGoBackDialog] = React.useState(false);

    const handleGoBackClick = () => {
        setOpenGoBackDialog(true);
    };

    const handleGoBackDialogConfirm = () => {
        setOpenGoBackDialog(false);

        let lastIndexWithUserInput = -1; // Initialize with -1 to indicate not found

        // Use a for loop to find the index of the last occurrence where has_user_input is true before the nth element
        // additional -1 because 1 based
        for (let i = currentOriginalDialogPromptIdIndex - 2; i >= 0; i--) {
            const targetId = originalDialogPromptIdList[i];
            // Use the find() method to search for the first element with the target ID
            const dialogPromptMap = dialogPromptList.find((dialogPrompt) => dialogPrompt.id === targetId);
            if (dialogPromptMap) {
                const dialgoPromptObject = DialogPromptFactory(
                    dialogPromptMap.class_name,
                    dialogPromptMap.id,
                    dialogPromptMap.dialog_id,
                    dialogPromptMap.order_index,
                    dialogPromptMap.is_teacher,
                    dialogPromptMap.prompt,
                    dialogPromptMap.is_note_to_student,
                    dialogPromptMap.is_hidden);

                if (dialgoPromptObject && dialgoPromptObject.has_user_input) {
                    lastIndexWithUserInput = i;
                    break; // Found the last occurrence, exit the loop
                }
            }
        }
        if (lastIndexWithUserInput < 0) lastIndexWithUserInput = 0;

        const repeatIdSlice = originalDialogPromptIdList.slice(lastIndexWithUserInput, currentOriginalDialogPromptIdIndex - 1);
        // Use the spread operator (...) to shallow copy the objects in the repeatSlice
        const copiedRepeatSlice = repeatIdSlice.map((dialogPromptId) => {
            // Use the find() method to search for the first element with the dialogPromptId
            const dialogPrompt = dialogPromptList.find((dialogPrompt) => dialogPrompt.id === dialogPromptId);
            // return copy of prompt
            return { ...dialogPrompt } as DialogPrompt
        });
        // insert right at current dialog prompt
        const i = currentDialogPromptIndex - 1;
        const updatedDialogPromptList = [...dialogPromptList.slice(0, i), ...copiedRepeatSlice, ...dialogPromptList.slice(i)];
        setDialogPromptList(updatedDialogPromptList);

        // don't forget to update original dialog prompt variables
        // +1 because 1-based
        setCurrentOriginalDialogPromptIdIndex(lastIndexWithUserInput + 1)

    };

    const handleGoBackDialogCancel = () => {
        setOpenGoBackDialog(false);
    };

    const handleModalClose = () => {
        setModalOpen(false);
    };

    const handleGoToBottom = () => {
        // Release ChatBubble auto-scroll lock if available
        try {
            const unlock = (window as any).__chatAutoScrollUnlock;
            if (typeof unlock === 'function') unlock();
            const globalUnlock = (window as any).__autoScrollLock_globalUnlock;
            if (typeof globalUnlock === 'function') globalUnlock();
        } catch { /* ignore */ }

        // Smoothly scroll the scroll container itself all the way to the bottom,
        try {
            if (scrollContainerRef.current) {
                const el = scrollContainerRef.current;
                el.dataset.agentAutoScrollLock = '0';
                el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
            } else if (chatBubbleContainerRef.current) {
                // Fallback: scroll sentinel into view if container ref is missing
                chatBubbleContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
            } else {
                // Last-resort fallback to page bottom
                const se = document.scrollingElement || document.documentElement;
                if (se) se.scrollTo({ top: se.scrollHeight, behavior: 'smooth' });
            }
        } catch { /* ignore */ }
    };

    React.useEffect(() => {
        if (currentDialogPromptIndex <= 1) {
            setLeftNavigationAlwaysList([
                <IconButton disabled aria-label="Go back" onClick={handleGoBackClick} sx={{ width: '100%', height: '100%' }}>
                    <img src={LeftArrowBlueIcon} style={{ height: '100%', width: 'auto', maxHeight: '24px', opacity: 0.2 }} />
                </IconButton>
            ])
        } else {
            setLeftNavigationAlwaysList([
                <IconButton aria-label="Go back" onClick={handleGoBackClick} sx={{ width: '100%', height: '100%' }}>
                    <img src={LeftArrowBlueIcon} style={{ height: '100%', width: 'auto', maxHeight: '24px' }} />
                </IconButton>
            ])
        }
    }, [currentDialogPromptIndex, setLeftNavigationAlwaysList])

    // icon size
    // "small"     20px    for compact UI
    // "medium"    24px    standard
    // "large"     36px    prominent icons
    // const theme = useTheme();
    // const isCompact = useMediaQuery(theme.breakpoints.down("sm"));

    // const navButtonSize = isCompact ? '20px' : '24px'
    // const actionButtonSize = isCompact ? '24px' : '36px'

    return (
        <DndProvider backend={HTML5Backend}>
            <Stack sx={{ width: '100%', margin: '0 auto', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <SentenceScoreDialogProvider>
                    <div
                        ref={scrollContainerRef}
                        data-testid="dialog-scroll-container"
                        data-agent-auto-scroll-container="true"
                        data-agent-dialog-scroll-container="true"
                        onScroll={handleScroll}
                        style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
                    >
                        {dialogPromptList.slice(0, currentDialogPromptIndex).map((dialogPrompt, index) => (
                            DialogPromptComponentFactory(
                                dialogPrompt,
                                setDialogPromptComplete,
                                chatBubbleContainerRef,
                                true,
                                false,
                                index === currentDialogPromptIndex - 1,
                            )
                        ))}
                        <div ref={chatBubbleContainerRef} />
                    </div>
                    <SentenceScoreDialog />
                </SentenceScoreDialogProvider>

                {/* FOOTER */}
                <Stack
                    direction="column"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ position: 'relative', flexShrink: 0 }}   /* 👈 make this the positioning context */
                >
                    {/* go-to-bottom button (conditionally shown) */}
                    {showScrollToBottom && (
                        <IconButton
                            onClick={handleGoToBottom}
                            size="small"
                            sx={{
                                position: 'absolute',
                                left: '50%',                  // ⬅️ center horizontally
                                bottom: 80,                   // adjust as needed
                                transform: 'translateX(-50%)',// ⬅️ perfect centering
                                zIndex: 1300,
                                backgroundColor: '#ffffffff',
                                width: 40,
                                height: 40,
                                boxShadow: 3,
                                '&:hover': { backgroundColor: '#abcdffff' },
                                border: '2px solid #fff',
                            }}
                            aria-label="Scroll to latest message"
                        >
                            <img src={DropDownArrow} alt="Go to bottom" style={{ width: 20, height: 20 }} />
                        </IconButton>
                    )}

                    {/* NAVIGATION buttons */}
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                        {/* LEFT navigation buttons */}
                        <Stack data-testid="dialog-left-nav" direction="row" alignItems="center" justifyContent="left" sx={{ flexGrow: 1 }} gap={1}>
                            {leftNavigationAlwaysList.map((leftNavAlways, index) => (
                                <Box key={index} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{leftNavAlways}</Box>
                            ))}
                            {leftNavigationList.map((leftNav, index) => (
                                <Box key={index} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{leftNav}</Box>
                            ))}
                        </Stack>
                        {/* RIGHT navigation buttons */}
                        <Stack data-testid="dialog-right-nav" direction="row" alignItems="center" justifyContent="right" sx={{ flexGrow: 1, pr: 0.5 }} gap={1}>
                            {rightNavigationList.map((rightNav, index) => (
                                <Box key={index} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{rightNav}</Box>
                            ))}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <FeedbackFooterButton shakeNonce={feedbackShakeNonce} />
                            </Box>
                        </Stack>
                    </Stack>

                    <Divider sx={{ width: '90%', borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }} />

                    {/* ACTION buttons */}
                    <Stack data-testid="dialog-action-area" direction="row" alignItems="center" justifyContent="center" flexWrap="wrap" rowGap={1} sx={{ flexGrow: 1, my: 1, width: '100%', minHeight: 60 }} gap={1}>
                        <Box sx={{ display: "flex", flexGrow: 1, justifyContent: "center", flexWrap: "wrap", gap: 1 }}>
                            {actionList.map((action, index) => (
                                <Box key={index} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{action}</Box>
                            ))}
                        </Box>
                    </Stack>

                    {/* Notes Dialog */}
                    <Modal
                        open={isModalOpen}
                        onClose={handleModalClose}
                        aria-labelledby="modal-title"
                        aria-describedby="modal-description"
                    >
                        <div
                            style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                backgroundColor: '#fff',
                                padding: '2rem',
                                outline: 'none',
                            }}
                        >
                            <Stack spacing={2} alignItems="center">
                                <Typography variant="h5" id="modal-title" style={{ paddingBottom: '1em' }}>
                                    Notes
                                </Typography>
                                {noteToStudent.map((note, index) => (
                                    <Typography variant="body1" id="modal-description" key={index}>
                                        {note}
                                    </Typography>
                                ))}
                            </Stack>
                        </div>
                    </Modal>
                    {/* Go Back Confirmation Dialog */}
                    <Dialog open={openGoBackDialog} onClose={handleGoBackDialogCancel}>
                        <DialogTitle><Typography variant="h4" sx={{ color: '#383838' }}>Confirm Repeat</Typography></DialogTitle>
                        <DialogContent>
                            <DialogContentText sx={{ color: '#8D8D8D' }}>
                                Are you sure you want to repeat the last exercise?
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions sx={{ justifyContent: 'space-between', '& .MuiButton-root': { flex: 1 } }}>
                            <Button onClick={handleGoBackDialogCancel} color="primary" sx={{ color: '#787878' }}>
                                Not Repeat
                            </Button>
                            <Button onClick={handleGoBackDialogConfirm} color="primary" sx={{ color: '#787878' }}>
                                Repeat
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Stack>
            </Stack>
        </DndProvider>
    );

};

export default DialogView;

export function DialogPromptComponentFactory(dialogPrompt: DialogPrompt,
    setDialogPromptComplete: (dialogPromptId: number) => void,
    chatBubbleContainerRef: React.RefObject<HTMLDivElement>,
    showAvatar: boolean = true,
    isSettings: boolean = false,
    isFooterActive: boolean = true) {
    const component = DialogPromptComponentFactoryImpl(
        dialogPrompt,
        setDialogPromptComplete,
        chatBubbleContainerRef,
        showAvatar,
        isSettings,
    );

    return (
        <DialogPromptFooterScope key={dialogPrompt.id} isActive={isFooterActive}>
            {component}
        </DialogPromptFooterScope>
    );
}

function DialogPromptComponentFactoryImpl(dialogPrompt: DialogPrompt,
    setDialogPromptComplete: (dialogPromptId: number) => void,
    chatBubbleContainerRef: React.RefObject<HTMLDivElement>,
    showAvatar: boolean = true,
    isSettings: boolean = false) {
    if (dialogPrompt.class_name == "DialogPromptModel") {
        return (<DialogPromptComponent dialogPrompt={dialogPrompt} setDialogPromptComplete={setDialogPromptComplete} key={dialogPrompt.id} showAvatar={showAvatar} chatBubbleContainerRef={chatBubbleContainerRef} isSettings={isSettings} />)
    } else if (dialogPrompt.class_name == "DialogPromptFeedbackButtonModel") {
        return (<DialogPromptFeedbackButtonComponent dialogPrompt={dialogPrompt as DialogPromptFeedbackButton} setDialogPromptComplete={setDialogPromptComplete} key={dialogPrompt.id} showAvatar={showAvatar} chatBubbleContainerRef={chatBubbleContainerRef} isSettings={isSettings} />)
    } else if (dialogPrompt.class_name == "DialogPromptEnterNameModel") {
        return (<DialogPromptEnterNameComponent dialogPrompt={dialogPrompt} setDialogPromptComplete={setDialogPromptComplete} key={dialogPrompt.id} showAvatar={showAvatar} chatBubbleContainerRef={chatBubbleContainerRef} isSettings={isSettings} />)
    } else if (dialogPrompt.class_name == "DialogPromptImageModel") {
        return (<DialogPromptImageComponent dialogPrompt={dialogPrompt as DialogPromptImage} setDialogPromptComplete={setDialogPromptComplete} key={dialogPrompt.id} showAvatar={showAvatar} chatBubbleContainerRef={chatBubbleContainerRef} isSettings={isSettings} />)
    } else if (dialogPrompt.class_name == "DialogPromptIntroduceWordModel") {
        return (<DialogPromptIntroduceWordComponent dialogPrompt={dialogPrompt as DialogPromptIntroduceWord} setDialogPromptComplete={setDialogPromptComplete} key={dialogPrompt.id} showAvatar={showAvatar} chatBubbleContainerRef={chatBubbleContainerRef} isSettings={isSettings} />)
    } else if (dialogPrompt.class_name == "DialogPromptLearnWithCharacterModel") {
        return (<DialogPromptLearnWithCharacterComponent dialogPrompt={dialogPrompt as DialogPromptLearnWithCharacter} setDialogPromptComplete={setDialogPromptComplete} key={dialogPrompt.id} showAvatar={showAvatar} chatBubbleContainerRef={chatBubbleContainerRef} isSettings={isSettings} />)
    } else if (dialogPrompt.class_name == "DialogPromptScoringLevelModel") {
        return (<DialogPromptScoringLevelComponent dialogPrompt={dialogPrompt as DialogPromptScoringLevel} setDialogPromptComplete={setDialogPromptComplete} key={dialogPrompt.id} showAvatar={showAvatar} chatBubbleContainerRef={chatBubbleContainerRef} isSettings={isSettings} />)
    } else if (dialogPrompt.class_name == "DialogPromptSelectAudioSpeedModel") {
        return (<DialogPromptSelectAudioSpeedComponent dialogPrompt={dialogPrompt as DialogPromptSelectAudioSpeed} setDialogPromptComplete={setDialogPromptComplete} key={dialogPrompt.id} showAvatar={showAvatar} chatBubbleContainerRef={chatBubbleContainerRef} isSettings={isSettings} />)
    } else if (dialogPrompt.class_name == "DialogPromptListenSpeakModel") {
        return (<DialogPromptListenSpeakComponent dialogPrompt={dialogPrompt as DialogPromptListenSpeak} setDialogPromptComplete={setDialogPromptComplete} key={dialogPrompt.id} showAvatar={showAvatar} chatBubbleContainerRef={chatBubbleContainerRef} isSettings={isSettings} />)
    } else if (dialogPrompt.class_name == "DialogPromptListenSpeakNoGradeModel") {
        return (<DialogPromptListenSpeakNoGradeComponent dialogPrompt={dialogPrompt as DialogPromptListenSpeakNoGrade} setDialogPromptComplete={setDialogPromptComplete} key={dialogPrompt.id} showAvatar={showAvatar} chatBubbleContainerRef={chatBubbleContainerRef} isSettings={isSettings} />)
    } else if (dialogPrompt.class_name == "DialogPromptQuizWordModel") {
        return (<DialogPromptQuizWordComponent dialogPrompt={dialogPrompt as DialogPromptQuizWord} setDialogPromptComplete={setDialogPromptComplete} key={dialogPrompt.id} showAvatar={showAvatar} chatBubbleContainerRef={chatBubbleContainerRef} isSettings={isSettings} />)
    } else if (dialogPrompt.class_name == "DialogPromptSelectAvatarModel") {
        return (<DialogPromptSelectAvatarComponent dialogPrompt={dialogPrompt} setDialogPromptComplete={setDialogPromptComplete} key={dialogPrompt.id} showAvatar={showAvatar} chatBubbleContainerRef={chatBubbleContainerRef} isSettings={isSettings} />)
    } else if (dialogPrompt.class_name == "DialogPromptSelectTeacherModel") {
        return (<DialogPromptSelectTeacherComponent dialogPrompt={dialogPrompt as DialogPromptSelectTeacher} setDialogPromptComplete={setDialogPromptComplete} key={dialogPrompt.id} showAvatar={showAvatar} chatBubbleContainerRef={chatBubbleContainerRef} isSettings={isSettings} />)
    } else if (dialogPrompt.class_name == "DialogPromptStartClassModel") {
        return (<DialogPromptStartClassComponent dialogPrompt={dialogPrompt as DialogPromptStartClass} setDialogPromptComplete={setDialogPromptComplete} key={dialogPrompt.id} showAvatar={showAvatar} chatBubbleContainerRef={chatBubbleContainerRef} isSettings={isSettings} />)
    } else if (dialogPrompt.class_name == "DialogPromptStartNextLessonModel") {
        return (<DialogPromptStartNextLessonComponent dialogPrompt={dialogPrompt as DialogPromptStartNextLesson} setDialogPromptComplete={setDialogPromptComplete} key={dialogPrompt.id} showAvatar={showAvatar} chatBubbleContainerRef={chatBubbleContainerRef} isSettings={isSettings} />)
    } else if (dialogPrompt.class_name == "DialogPromptSummaryModel") {
        return (<DialogPromptSummaryComponent dialogPrompt={dialogPrompt as DialogPromptSummary} setDialogPromptComplete={setDialogPromptComplete} key={dialogPrompt.id} showAvatar={showAvatar} chatBubbleContainerRef={chatBubbleContainerRef} isSettings={isSettings} />)
    } else if (dialogPrompt.class_name == "DialogPromptTypeSpeakModel") {
        return (<DialogPromptTypeSpeakComponent dialogPrompt={dialogPrompt as DialogPromptTypeSpeak} setDialogPromptComplete={setDialogPromptComplete} key={dialogPrompt.id} showAvatar={showAvatar} chatBubbleContainerRef={chatBubbleContainerRef} isSettings={isSettings} />)
    } else if (dialogPrompt.class_name == "DialogPromptTypeSpeakV2Model") {
        return (<DialogPromptTypeSpeakV2Component dialogPrompt={dialogPrompt as DialogPromptTypeSpeakV2} setDialogPromptComplete={setDialogPromptComplete} key={dialogPrompt.id} showAvatar={showAvatar} chatBubbleContainerRef={chatBubbleContainerRef} isSettings={isSettings} />)
    } else if (dialogPrompt.class_name == "DialogPromptMatchWordPictureModel") {
        return (<DialogPromptMatchWordPictureComponent dialogPrompt={dialogPrompt as DialogPromptMatchWordPicture} setDialogPromptComplete={setDialogPromptComplete} key={dialogPrompt.id} showAvatar={showAvatar} chatBubbleContainerRef={chatBubbleContainerRef} isSettings={isSettings} />)
    } else if (dialogPrompt.class_name == "DialogPromptMatchGameModel") {
        return (<DialogPromptMatchGameComponent dialogPrompt={dialogPrompt as DialogPromptMatchGame} setDialogPromptComplete={setDialogPromptComplete} key={dialogPrompt.id} showAvatar={showAvatar} chatBubbleContainerRef={chatBubbleContainerRef} isSettings={isSettings} />)
    } else if (dialogPrompt.class_name == "DialogPromptImageSelectSpeakModel") {
        return (<DialogPromptImageSelectSpeakComponent dialogPrompt={dialogPrompt as DialogPromptImageSelectSpeak} setDialogPromptComplete={setDialogPromptComplete} key={dialogPrompt.id} showAvatar={showAvatar} chatBubbleContainerRef={chatBubbleContainerRef} isSettings={isSettings} />)
    } else if (dialogPrompt.class_name == "DialogPromptQuizQuestionModel") {
        return (<DialogPromptQuizQuestionComponent dialogPrompt={dialogPrompt as DialogPromptQuizQuestion} setDialogPromptComplete={setDialogPromptComplete} key={dialogPrompt.id} showAvatar={showAvatar} chatBubbleContainerRef={chatBubbleContainerRef} isSettings={isSettings} />)
    } else if (dialogPrompt.class_name == "DialogPromptQuizQuestionAudioModel") {
        return (<DialogPromptQuizQuestionAudioComponent dialogPrompt={dialogPrompt as DialogPromptQuizQuestionAudio} setDialogPromptComplete={setDialogPromptComplete} key={dialogPrompt.id} showAvatar={showAvatar} chatBubbleContainerRef={chatBubbleContainerRef} isSettings={isSettings} />)
    } else if (dialogPrompt.class_name == "DialogPromptQuizQuestionSpeakModel") {
        return (<DialogPromptQuizQuestionSpeakComponent dialogPrompt={dialogPrompt as DialogPromptQuizQuestionSpeak} setDialogPromptComplete={setDialogPromptComplete} key={dialogPrompt.id} showAvatar={showAvatar} chatBubbleContainerRef={chatBubbleContainerRef} isSettings={isSettings} />)
    } else if (dialogPrompt.class_name == "DialogPromptConversationListenModel") {
        return (<DialogPromptConversationListenComponent dialogPrompt={dialogPrompt as DialogPromptConversationListen} setDialogPromptComplete={setDialogPromptComplete} key={dialogPrompt.id} showAvatar={showAvatar} chatBubbleContainerRef={chatBubbleContainerRef} isSettings={isSettings} />)
    } else if (dialogPrompt.class_name == "DialogPromptConversationListenSpeakModel") {
        return (<DialogPromptConversationRelatedComponent dialogPrompt={dialogPrompt as DialogPromptConversationListenSpeak} setDialogPromptComplete={setDialogPromptComplete} key={dialogPrompt.id} showAvatar={showAvatar} chatBubbleContainerRef={chatBubbleContainerRef} isSettings={isSettings} />)
    } else if (dialogPrompt.class_name == "DialogPromptConversationListenWithoutCharacterModel") {
        return (<DialogPromptConversationListenComponent dialogPrompt={dialogPrompt as DialogPromptConversationListenWithoutCharacter} setDialogPromptComplete={setDialogPromptComplete} key={dialogPrompt.id} showAvatar={showAvatar} chatBubbleContainerRef={chatBubbleContainerRef} isSettings={isSettings} />)
    } else if (dialogPrompt.class_name == "DialogPromptConversationReadSpeakModel") {
        return (<DialogPromptConversationRelatedComponent dialogPrompt={dialogPrompt as DialogPromptConversationReadSpeak} setDialogPromptComplete={setDialogPromptComplete} key={dialogPrompt.id} showAvatar={showAvatar} chatBubbleContainerRef={chatBubbleContainerRef} isSettings={isSettings} />)
    } else if (dialogPrompt.class_name == "DialogPromptConversationReadWithBlankSpeakModel") {
        return (<DialogPromptConversationRelatedComponent dialogPrompt={dialogPrompt as DialogPromptConversationReadWithBlankSpeak} setDialogPromptComplete={setDialogPromptComplete} key={dialogPrompt.id} showAvatar={showAvatar} chatBubbleContainerRef={chatBubbleContainerRef} isSettings={isSettings} />)
    } else if (dialogPrompt.class_name == "DialogPromptConversationSpeakOnlyModel") {
        return (<DialogPromptConversationRelatedComponent dialogPrompt={dialogPrompt as DialogPromptConversationSpeakOnly} setDialogPromptComplete={setDialogPromptComplete} key={dialogPrompt.id} showAvatar={showAvatar} chatBubbleContainerRef={chatBubbleContainerRef} isSettings={isSettings} />)
    } else if (dialogPrompt.class_name == "DialogPromptSelectNextDialogModel") {
        return (<DialogPromptSelectNextDialogComponent dialogPrompt={dialogPrompt as DialogPromptSelectNextDialog} setDialogPromptComplete={setDialogPromptComplete} key={dialogPrompt.id} showAvatar={showAvatar} chatBubbleContainerRef={chatBubbleContainerRef} isSettings={isSettings} />)
    } else if (dialogPrompt.class_name == "DialogPromptSpeakNoGradeModel") {
        return (<DialogPromptSpeakNoGradeComponent dialogPrompt={dialogPrompt as DialogPromptSpeakNoGrade} setDialogPromptComplete={setDialogPromptComplete} key={dialogPrompt.id} showAvatar={showAvatar} chatBubbleContainerRef={chatBubbleContainerRef} isSettings={isSettings} />)
    } else if (dialogPrompt.class_name == "DialogPromptSpeakWithGradeModel") {
        return (<DialogPromptSpeakWithGradeComponent dialogPrompt={dialogPrompt as DialogPromptSpeakWithGrade} setDialogPromptComplete={setDialogPromptComplete} key={dialogPrompt.id} showAvatar={showAvatar} chatBubbleContainerRef={chatBubbleContainerRef} isSettings={isSettings} />)
    } else {
        throw new Error("unknown class name " + dialogPrompt.class_name)
    }
}
